import './tooltip.less';
import { bubbleDialogComponentModule } from '../bubble-dialog/bubble-dialog';
import { stopEvent } from '../../utils/event-utils';

export const tooltipComponentModule = angular
  .module('Q.components.tooltip', [bubbleDialogComponentModule])

  /**
   * @ngdoc directive
   * @name tooltip
   * @description
   * A popover tooltip shown on mouseover
   * @element ANY
   * @param {template} tooltip - Text of the tooltip
   * @param {template} alt - Text of the tooltip unless specified by {@param tooltip}
   * @param {template} tooltipPosition - Position of the tooltip
   * @param {template} tooltipDelay - Adjust how long to wait before showing, defaults to 600
   * @param {template} tooltipShortcutHint - If provided, show a shortcut key hint
   *
   * @example
   * <example>
   * <div class="btn btn-default" tooltip="Simple Tooltip">Hover me!</div>
   * </example>
   */
  .directive('tooltip', function (bubbleDialogService, $document, $sce) {
    let lastInstance;

    return {
      restrict: 'A',
      controller: function () {
        let ctrl = this;
        ctrl.instance = null;
        ctrl.disabled = false;
        ctrl.setDisabled = (disabled) => {
          ctrl.disabled = disabled;
          if (disabled && ctrl.instance) {
            ctrl.instance.close();
            ctrl.instance = null;
          }
        };
      },
      require: ['tooltip', '?tooltipHelpIcon'],
      link: function (
        scope,
        element,
        attrs,
        [tooltipCtrl, tooltipHelpIconCtrl],
      ) {
        const getContent = () => attrs.tooltip || attrs.alt;

        const isOpen = (tooltipCtrl.isOpen = () => {
          return (
            getContent() &&
            !tooltipCtrl.disabled &&
            tooltipCtrl.instance &&
            !tooltipCtrl.instance.closed
          );
        });

        const open = (tooltipCtrl.open = (delay = 0) => {
          if (getContent() && !tooltipCtrl.disabled) {
            if (isOpen()) {
              if (
                delay === 0 &&
                tooltipCtrl.instance &&
                lastInstance === tooltipCtrl.instance
              ) {
                tooltipCtrl.instance.showNow();
              }
            } else {
              if (lastInstance) {
                scope.$applyAsync(lastInstance.close);
              }
              let content = getContent();
              if (attrs.tooltipShortcutHint) {
                content = $sce.trustAsHtml(
                  `<span class="tooltip-shortcut"><span>${content}</span><span class="shortcut-hint">${attrs.tooltipShortcutHint}</span></span>`,
                );
              }
              tooltipCtrl.instance = lastInstance = bubbleDialogService.open(
                scope,
                element,
                {
                  closeable: true,
                  content,
                  position: attrs.tooltipPosition,
                  positionArrow: tooltipCtrl.helpIconElement,
                  className: 'tooltip',
                  delay,
                },
              );
            }
          }
        });

        const close = (tooltipCtrl.close = () => {
          if (tooltipCtrl.instance) {
            tooltipCtrl.instance.close();
            tooltipCtrl.instance = null;
          }
        });

        tooltipCtrl.toggle = (delay = 0) => {
          if (isOpen()) {
            close();
          } else {
            open(delay);
          }
        };

        const delay = () =>
          +(attrs.tooltipDelay || (tooltipHelpIconCtrl ? 0 : 600));

        element.on('mouseenter', () => open(delay())).on('mouseleave', close);

        scope.$on('$destroy', close);
      },
    };
  })

  // A simple component that renders a question mark in a circle, which can be used for highlighting a tooltip
  // If added below a tooltip, it enables that tooltip to be opened explicitly by clicking
  // It can also be used as the source of a tooltip (with the same click-to-show behavior)
  .component('tooltipHelpIcon', {
    require: {
      tooltipCtrl: '^tooltip',
    },
    template: '',
    controller: function ($element, $scope) {
      const ctrl = this;
      ctrl.$onInit = () => {
        ctrl.tooltipCtrl.helpIconElement = $element;
      };
      $element.on('mouseenter', (event) => {
        if ($element.attr('tooltip')) return;
        stopEvent(event);
        $scope.$applyAsync(() => ctrl.tooltipCtrl.open());
      });
      $element.on('click', (event) => {
        stopEvent(event);
        $scope.$applyAsync(() => ctrl.tooltipCtrl.toggle());
      });
    },
  })

  .directive('dropdownToggle', function () {
    return {
      restrict: 'A',
      require: ['?^dropdown', '?^tooltip'],
      link(scope, el, attr, [dropdownCtrl, tooltipCtrl]) {
        if (dropdownCtrl && tooltipCtrl) {
          scope.$watch(() => dropdownCtrl.isOpen(), tooltipCtrl.setDisabled);
        }
      },
    };
  });
